import { Link } from "@tanstack/solid-router";
import { type Component, Show } from "solid-js";
import { cn } from "@listto/shared";
import { Skeleton } from "@/components/ui/skeleton";
import { CounterBadge } from "./counter-badge";

interface SidebarNavItemProps {
  to: string;
  label: string;
  icon: Component<{ class?: string }>;
  count?: number;
  /** When true, renders a small count-badge skeleton instead of the count. */
  countLoading?: boolean;
  exact?: boolean;
  onClick?: () => void;
}

const activeClass = "bg-accent/10 text-accent font-semibold";
const inactiveClass =
  "text-muted-foreground/80 hover:bg-accent/5 hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/30 focus-visible:bg-accent/5 transition-[background-color,color,box-shadow]";

export function SidebarNavItem(props: SidebarNavItemProps) {
  return (
    <Link
      to={props.to}
      onClick={props.onClick}
      class={cn(
        "group flex items-center justify-between rounded-md px-3 py-2.5 text-sm font-medium transition-[background-color,color,transform]",
        inactiveClass,
      )}
      activeProps={{
        class: cn(
          "group flex items-center justify-between rounded-md px-3 py-2.5 text-sm font-medium transition-colors",
          activeClass,
        ),
      }}
      activeOptions={{ exact: props.exact }}
    >
      <div class="flex items-center gap-2.5 transition-transform hover:translate-x-1 active:scale-[0.98]">
        <props.icon class="h-4 w-4 shrink-0" />
        {props.label}
      </div>
      <Show
        when={props.countLoading}
        fallback={
          <CounterBadge
            count={props.count}
            class="text-muted-foreground group-hover:text-foreground transition-colors"
          />
        }
      >
        <Skeleton class="h-4 w-5 rounded-full bg-muted/25" />
      </Show>
    </Link>
  );
}
